import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AttendanceStatus } from './admin';
import { ApiError, requestJson } from './client';
import { authHeaders } from './regulations';

export const ATTENDANCE_PERIODS = [
  { days: 7, label: 'Неделя' },
  { days: 30, label: '30 дней' },
  { days: 90, label: 'Квартал' },
] as const;
export type AttendancePeriod = (typeof ATTENDANCE_PERIODS)[number]['days'];

export interface AttendanceDayView {
  date: string;
  /** Суббота или воскресенье. */
  weekend: boolean;
  /** День ещё идёт: последняя отметка — не уход. */
  open: boolean;
  /** Опоздание в минутах от начала рабочего дня; null — вовремя или графика нет. */
  lateMinutes: number | null;
  /** Приход — первая отметка дня. */
  firstAt: string | null;
  /** Приход не отмечен турникетом: за приход принята первая отметка дня. */
  assumedArrival?: boolean;
  /** Уход не отмечен турникетом: за уход принята последняя отметка дня. */
  assumedDeparture?: boolean;
  /** Уход — последний проход, если он выход; null — нет отметки ухода. */
  lastAt: string | null;
  /** Последний проход дня (у идущего дня — «пока»). */
  latestAt: string;
  /** В офисе за день, минут; null — день идёт или проходов нечётное число. */
  minutes: number | null;
  /** Проходы: первый — вход, дальше выход, вход и так далее. */
  passes: { time: string; kind: 'in' | 'out' }[];
  /** Сумма отрезков «вход → выход», минут. */
  insideMinutes: number;
  /** Выходы в течение дня: «выход → снова вход». */
  breaks: { from: string; to: string; minutes: number }[];
  outsideMinutes: number;
  /** Одна или несколько отметок пропущены: нет ухода, нет прихода или две подряд в одну сторону. */
  unpaired: boolean;
  /** Пропущенных отметок: две подряд в одну сторону. */
  missed?: number;
  /** Направление проходов известно из отчёта, а не угадано по очерёдности. */
  known?: boolean;
  readings: string[];
  readsCount: number;
  truncated: boolean;
}

export interface AttendanceSummary {
  days: number;
  /** Рабочих дней с отметками. */
  workdays: number;
  /** Рабочих дней в периоде, за которые есть данные. */
  expectedWorkdays: number;
  /** Прошедшие рабочие дни без отметок. */
  missedWorkdays: string[];
  weekendDays: number;
  avgFirst: string | null;
  avgLast: string | null;
  avgMinutes: number | null;
  /** Среднее число выходов за рабочий день. */
  avgExits: number | null;
  /** Среднее время вне офиса за рабочий день, минут. */
  avgOutsideMinutes: number | null;
  /** Завершённых дней с нечётным числом проходов. */
  unpairedDays: number;
  /** null — график не задан. */
  lateDays: number | null;
  /** Доля рабочих дней с приходом вовремя, %. */
  onTimeShare: number | null;
  byWeekday: { weekday: string; days: number; avgFirst: string | null; avgLast: string | null }[];
}

export interface AttendanceSchedule {
  workStart: string | null;
  lateAfter: string | null;
}

export interface EmployeeAttendance {
  linked: boolean;
  schedule: AttendanceSchedule | null;
  /** Первая и последняя даты, за которые в системе есть данные. */
  coverage: { from: string; to: string } | null;
  /** Сегодняшний день, если уже есть отметки. */
  today: AttendanceDayView | null;
  from: string;
  to: string;
  summary: AttendanceSummary;
  items: AttendanceDayView[];
}

export interface DayStats {
  tracked: number;
  present: number;
  absent: number;
  /** Завершённый день с нечётным числом проходов. */
  unpaired: number;
  /** null — ни у кого нет графика. */
  late: number | null;
  avgFirst: string | null;
  avgLast: string | null;
  periodAvgMinutes: number | null;
}

export interface AttendanceDashboard {
  mode: 'team' | 'self' | 'none';
  canManage: boolean;
  admin: AttendanceStatus | null;
  date?: string;
  weekend?: boolean;
  open?: boolean;
  latestDate?: string | null;
  from?: string;
  scopes?: { id: string; name: string; parentId: string | null; depth: number; people: number }[];
  department?: { id: string; name: string; path: { id: string; name: string }[] };
  totals?: DayStats & { untracked: number };
  units?: (DayStats & { id: string; name: string })[];
  trend?: { date: string; weekend: boolean; present: number }[];
  people?: {
    id: string;
    fullName: string;
    position: string | null;
    photoUrl: string | null;
    status: 'present' | 'unpaired' | 'absent';
    schedule: AttendanceSchedule | null;
    today: AttendanceDayView | null;
    period: AttendanceSummary;
  }[];
}

export interface AttendanceUploadResult {
  duplicate: boolean;
  rows: number;
  inserted: number;
  updated: number;
  unchanged: number;
  ignored: number;
  errorCount: number;
  errors: { row: number; message: string }[];
  unmatchedPeople: number | null;
  source: { id: string; name: string };
}

export function useEmployeeAttendance(employeeId: string, days: AttendancePeriod) {
  return useQuery({
    queryKey: ['attendance', 'employee', employeeId, days],
    queryFn: () => requestJson<EmployeeAttendance>(`/api/v1/employees/${employeeId}/attendance?days=${days}`),
  });
}

export function useAttendanceDashboard(input: {
  date: string | null;
  days: AttendancePeriod;
  departmentId: string | null;
}) {
  const query = new URLSearchParams({ days: String(input.days) });
  if (input.date) query.set('date', input.date);
  if (input.departmentId) query.set('departmentId', input.departmentId);
  return useQuery({
    queryKey: ['attendance', 'dashboard', input.date, input.days, input.departmentId],
    queryFn: () => requestJson<AttendanceDashboard>(`/api/v1/attendance?${query}`),
  });
}

/** Ручная загрузка отчёта системы проходов: тело — сам файл. */
export async function uploadAttendance(file: File, sourceId: string | null): Promise<AttendanceUploadResult> {
  const response = await fetch(`/api/v1/attendance/upload${sourceId ? `?sourceId=${sourceId}` : ''}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/octet-stream',
      'x-file-name': encodeURIComponent(file.name),
      'x-goodway-csrf': '1',
      ...authHeaders(),
    },
    body: file,
  });
  const body = (await response.json().catch(() => null)) as
    | (AttendanceUploadResult & { error?: { message?: string } })
    | null;
  if (!response.ok)
    throw new ApiError(response.status, 'BAD_REQUEST', body?.error?.message ?? 'Отчёт не загружен');
  return body as AttendanceUploadResult;
}

export interface AttendanceMappingResult {
  linked: number;
  errorCount: number;
  errors: { row: number; message: string }[];
  source: { id: string; name: string };
}

/** Таблица сопоставлений «система проходов → Bitrix24» с графиком (xlsx или csv). */
export async function uploadAttendanceMapping(
  file: File,
  sourceId: string | null,
): Promise<AttendanceMappingResult> {
  const response = await fetch(`/api/v1/attendance/mapping${sourceId ? `?sourceId=${sourceId}` : ''}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/octet-stream',
      'x-file-name': encodeURIComponent(file.name),
      'x-goodway-csrf': '1',
      ...authHeaders(),
    },
    body: file,
  });
  const body = (await response.json().catch(() => null)) as
    | (AttendanceMappingResult & { error?: { message?: string } })
    | null;
  if (!response.ok)
    throw new ApiError(response.status, 'BAD_REQUEST', body?.error?.message ?? 'Таблица не загружена');
  return body as AttendanceMappingResult;
}

export function useLinkAttendancePerson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { sourceId: string; deviceId: string; employeeId: string | null }) =>
      requestJson(`/api/v1/attendance/people/${input.sourceId}/${encodeURIComponent(input.deviceId)}`, {
        method: 'PUT',
        body: JSON.stringify({ employeeId: input.employeeId }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['attendance'] }),
  });
}

const DAY_NAMES = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

/** «18.09, чт». */
export const dayLabel = (date: string) =>
  `${date.slice(8, 10)}.${date.slice(5, 7)}, ${DAY_NAMES[new Date(`${date}T00:00:00Z`).getUTCDay()] ?? ''}`;

/** «9 ч 05 мин», «46 мин». */
export const duration = (minutes: number | null) => {
  if (minutes === null) return '—';
  const hours = Math.floor(minutes / 60);
  return hours ? `${hours} ч ${String(minutes % 60).padStart(2, '0')} мин` : `${minutes % 60} мин`;
};

/** «09:05:33» → «09:05». */
export const shortTime = (time: string | null) => (time ? time.slice(0, 5) : '—');

/** Отчёты не приходили дольше суток (с запасом на выходные ночи — 26 часов). */
export const isStale = (receivedAt: string | undefined, now = Date.now()) =>
  !receivedAt || now - new Date(receivedAt).getTime() > 26 * 3_600_000;
